
import { supabase } from '@/integrations/supabase/client';
import { logErrorToast } from '@/utils/toastLogger';
import { JobSearchResult } from '@/types/jobSearch'; // Import JobSearchResult

/**
 * Ensures that a job exists in the job_search_results table before saving.
 * If the job doesn't exist, it creates a corresponding job_search_queries record
 * and then inserts the job into job_search_results.
 * @returns The job search result ID if found or inserted
 */
export const ensureJobExists = async (jobData: JobSearchResult, userId: string): Promise<string | null> => {
  if (!jobData?.id) {
    console.error('[ensureJobExists] Missing job ID in job data:', jobData);
    return null;
  }
  if (!userId) {
    console.error('[ensureJobExists] Missing userId');
    return null;
  }

  try {
    // Check if job already exists
    const { data: existingJob, error: queryError } = await supabase
      .from('job_search_results')
      .select('id')
      .eq('id', jobData.id)
      .maybeSingle();

    if (queryError && queryError.message && !queryError.message.includes('no rows') && !queryError.message.includes('JSON object requested, but got null')) {
      console.error('[ensureJobExists] Error checking job existence:', queryError);
      throw queryError;
    }

    if (existingJob) {
      console.log('[ensureJobExists] Job already exists:', existingJob.id);
      return existingJob.id;
    }

    console.log('[ensureJobExists] Job not found, creating placeholder query and inserting job to database:', jobData.id);
    
    const queryParamsForDirectSave = {
      providerId: "direct_save",
      source: jobData.source || "direct_save", // Use jobData.source if available
      jobId: jobData.id,
      jobTitle: jobData.title || "N/A", 
      timestamp: new Date().toISOString()
    };

    const { data: queryData, error: queryInsertError } = await supabase
      .from('job_search_queries')
      .insert({
        user_id: userId,
        query_params: queryParamsForDirectSave,
      })
      .select('id')
      .single();

    if (queryInsertError) {
      console.error('[ensureJobExists] Error inserting direct save query:', queryInsertError);
      throw queryInsertError;
    }
    const newQueryId = queryData.id;
    console.log('[ensureJobExists] Successfully created placeholder query:', newQueryId);
    
    let scoreToStore: number | null = null;
    if (jobData.score !== undefined && jobData.score !== null && !isNaN(Number(jobData.score))) {
      scoreToStore = Number(jobData.score);
    } else {
      console.warn(`[ensureJobExists] Job ID ${jobData.id} has missing or invalid score:`, jobData.score, '- Storing score as null.');
    }

    const jobUrlToStore = jobData.job_url || jobData.url || jobData.raw_data?.job_url || jobData.raw_data?.url;
    const snippetToStore = jobData.snippet || (jobData.description && typeof jobData.description === 'string' ? jobData.description.substring(0, 250) + (jobData.description.length > 250 ? '...' : '') : undefined);


    const jobToInsert = {
      id: jobData.id, 
      job_title: jobData.title, 
      company_name: jobData.company_name,
      location: jobData.location,
      job_url: jobUrlToStore,
      source: jobData.source,
      snippet: snippetToStore,
      posted_date: jobData.posted_date,
      raw_data: jobData, // This stores the full jobData object, including benefits, qualifications etc.
      status: jobData.status || 'active', 
      provider_id: jobData.provider_id || 'direct_save', 
      query_id: newQueryId, 
      relevance_explanation: jobData.relevance_explanation || 'Job saved directly by user.',
      score: scoreToStore,
      // The following fields are not direct columns in 'job_search_results'
      // and will be stored within the 'raw_data' JSONB field.
      // benefits: jobData.benefits || jobData.raw_data?.benefits, // Removed
      // qualification_details: jobData.qualification_details || jobData.raw_data?.qualification_details, // Removed
      // responsibility_details: jobData.responsibility_details || jobData.raw_data?.responsibility_details, // Removed
      // employment_type: jobData.employment_type || jobData.raw_data?.employment_type, // Removed
      // experience_level: jobData.experience_level, // Removed
      // salary_range: jobData.salary_range, // Removed
    };
    
    console.log('[ensureJobExists] Job object to be inserted:', JSON.stringify(jobToInsert, null, 2));

    const { data: insertedJob, error: insertError } = await supabase
      .from('job_search_results')
      .insert(jobToInsert)
      .select('id')
      .single();

    if (insertError) {
      console.error('[ensureJobExists] Error inserting job after creating query:', insertError);
      if (insertError.message.includes('array value must start with "{" or dimension information')) {
          console.error('[ensureJobExists] Potential type mismatch for array field (e.g. benefits). DB column might need to be JSONB or TEXT[]. JobData benefits:', jobData.benefits);
      }
      throw insertError;
    }

    console.log('[ensureJobExists] Successfully inserted job with placeholder query:', insertedJob);
    return insertedJob.id;
  } catch (e) {
    console.error('[ensureJobExists] General error:', e);
    logErrorToast('Failed to ensure job existence in database.');
    return null;
  }
};
